import { Upload, Youtube, Download, RefreshCw, FileSpreadsheet } from "lucide-react";

export const ImportTab = ({ uploadMeta, setUploadMeta, uploading, uploadPct, videoFileRef, uploadVideoFile, fileRef, importing, doImport,
  ytText, setYtText, ytBusy, bulkYoutube, exportCatalogue, importCatalogue, catFileRef,
  xlsxRef, xlsxBusy, importSpreadsheet }) => {
  const uploadInput = (name, ph, required = false) => (
    <input data-testid={`upload-${name}`} value={uploadMeta[name]} placeholder={ph + (required ? " *" : "")}
      onChange={(e) => setUploadMeta({ ...uploadMeta, [name]: e.target.value })}
      className="w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-3 py-2 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm" />
  );

  return (
    <div className="space-y-8 max-w-2xl">
      <div className="border border-[#C5A059]/40 bg-[#15130d] p-8" data-testid="import-spreadsheet-card">
        <div className="flex items-center gap-3 mb-3">
          <FileSpreadsheet size={22} className="text-[#C5A059]" />
          <h3 className="font-serif text-2xl text-[#EBE9E1]">Import Spreadsheet (.xlsx)</h3>
        </div>
        <p className="text-[#8A8F98] text-sm mb-6 leading-relaxed">
          Upload the master archive spreadsheet (<code className="text-[#C5A059]">Chiba_Sensei_Archive_Database.xlsx</code>) to update the
          catalogue in one step. It <strong className="text-[#C5A059]">adds new records and enhances existing ones</strong> — matched by Archive ID,
          YouTube ID, then title — so it never creates duplicates. Streamable platforms are auto-detected.
        </p>
        <input ref={xlsxRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          data-testid="import-spreadsheet-file" onChange={(e) => importSpreadsheet(e.target.files?.[0])} className="hidden" />
        <button data-testid="import-spreadsheet-btn" onClick={() => xlsxRef.current?.click()} disabled={xlsxBusy}
          className="btn-primary px-8 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
          <FileSpreadsheet size={16} /> {xlsxBusy ? "Importing spreadsheet…" : "Choose Spreadsheet & Import"}
        </button>
        <p className="text-[#555] text-xs mt-4">Reads the "Master Archive" sheet. Featured status, newsletter history and original dates on existing records are preserved.</p>
      </div>

      <div className="border border-white/10 bg-[#121418] p-8" data-testid="bulk-youtube-card">
        <div className="flex items-center gap-3 mb-3">
          <Youtube size={22} className="text-[#C5A059]" />
          <h3 className="font-serif text-2xl text-[#EBE9E1]">Bulk Add YouTube Videos &amp; Playlists</h3>
        </div>
        <p className="text-[#8A8F98] text-sm mb-4 leading-relaxed">
          Paste YouTube <strong className="text-[#C5A059]">video links</strong> and/or <strong className="text-[#C5A059]">playlist links</strong>
          (one per line, or comma/space separated). Playlists are expanded to every video, and each video's
          <strong className="text-[#C5A059]"> title, uploader and duration</strong> is fetched automatically. Links already in the library are skipped.
        </p>
        <textarea data-testid="bulk-youtube-input" value={ytText} onChange={(e) => setYtText(e.target.value)} rows={5}
          placeholder={"https://www.youtube.com/watch?v=...\nhttps://youtu.be/...\nhttps://www.youtube.com/playlist?list=..."}
          className="w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-3 py-2 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm mb-4 font-mono" />
        <button data-testid="bulk-youtube-btn" onClick={bulkYoutube} disabled={ytBusy}
          className="btn-primary px-8 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
          <Youtube size={16} /> {ytBusy ? "Fetching & adding…" : "Fetch & Add Videos"}
        </button>
        <p className="text-[#555] text-xs mt-4">Each video is looked up on YouTube, so large playlists take a moment. Up to 50 new videos are added per run — if a playlist has more, just click again to continue where it left off.</p>
      </div>

      <div className="border border-white/10 bg-[#121418] p-8" data-testid="upload-video-card">
        <h3 className="font-serif text-2xl text-[#EBE9E1] mb-3">Upload a Video File</h3>
        <p className="text-[#8A8F98] text-sm mb-6 leading-relaxed">
          Self-host a recording (mp4, webm, mov, m4v). It is stored securely and streams directly in the
          library — no third-party platform needed. Ideal for digitized footage you control.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {uploadInput("title", "Title", true)}
          {uploadInput("subject", "Subject / technique")}
          {uploadInput("recording_date", "Recording date (e.g. 1994)")}
          {uploadInput("location", "Location / Dojo")}
          {uploadInput("duration", "Duration (e.g. 42:15)")}
          {uploadInput("source", "Source / contributor")}
          {uploadInput("tags", "Tags (comma separated)")}
          {uploadInput("description", "Description")}
        </div>
        <input ref={videoFileRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
          data-testid="upload-video-file" onChange={(e) => uploadVideoFile(e.target.files?.[0])} className="hidden" />
        <button data-testid="upload-video-btn" onClick={() => videoFileRef.current?.click()} disabled={uploading}
          className="btn-primary px-8 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
          <Upload size={16} /> {uploading ? `Uploading… ${uploadPct}%` : "Choose Video & Upload"}
        </button>
        {uploading && (
          <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${uploadPct}%` }} />
          </div>
        )}
      </div>

      <div className="border border-white/10 bg-[#121418] p-8">
        <h3 className="font-serif text-2xl text-[#EBE9E1] mb-3">Bulk Import from CSV</h3>
        <p className="text-[#8A8F98] text-sm mb-4 leading-relaxed">
          Upload a CSV to add many videos at once. The importer recognises these columns (case-insensitive):
        </p>
        <div className="text-[#8A8F98] text-xs bg-[#0A0B0E] border border-white/10 p-4 mb-6 leading-relaxed">
          <code className="text-[#C5A059]">Title</code> (required), <code className="text-[#C5A059]">URL</code> or <code className="text-[#C5A059]">YouTube ID</code>,
          Channel/Owner, Recording Date, Year, Duration, Location/Dojo, Subject, Tags (semicolon-separated),
          Description, Source, Collection, Notes, Archive ID
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" data-testid="admin-import-file"
          onChange={(e) => doImport(e.target.files?.[0])} className="hidden" />
        <button data-testid="admin-import-btn" onClick={() => fileRef.current?.click()} disabled={importing}
          className="btn-primary px-8 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
          <Upload size={16} /> {importing ? "Importing…" : "Choose CSV & Import"}
        </button>
        <p className="text-[#555] text-xs mt-4">Rows without a title are skipped. YouTube, Vimeo, Facebook and Instagram links are auto-detected as streamable; you can refine any entry afterward in the Videos tab.</p>
      </div>

      <div className="border border-white/10 bg-[#121418] p-8" data-testid="catalogue-sync-card">
        <div className="flex items-center gap-3 mb-3">
          <RefreshCw size={20} className="text-[#C5A059]" />
          <h3 className="font-serif text-2xl text-[#EBE9E1]">Full Catalogue Sync</h3>
        </div>
        <p className="text-[#8A8F98] text-sm mb-6 leading-relaxed">
          Keep environments matched. <strong className="text-[#C5A059]">Export</strong> the entire catalogue here, then
          <strong className="text-[#C5A059]"> Import</strong> that file in the other environment. Import is idempotent — it updates
          existing records (matched by ID) and adds new ones, so it never creates duplicates.
        </p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="catalogue-export-btn" onClick={exportCatalogue}
            className="btn-primary px-6 py-3 font-medium flex items-center gap-2">
            <Download size={16} /> Export Catalogue (JSON)
          </button>
          <input ref={catFileRef} type="file" accept=".json,application/json" data-testid="catalogue-import-file"
            onChange={(e) => importCatalogue(e.target.files?.[0])} className="hidden" />
          <button data-testid="catalogue-import-btn" onClick={() => catFileRef.current?.click()}
            className="px-6 py-3 font-medium flex items-center gap-2 border border-white/20 text-[#EBE9E1] hover:border-[#C5A059] rounded-sm transition-colors">
            <Upload size={16} /> Import Catalogue (JSON)
          </button>
        </div>
        <p className="text-[#555] text-xs mt-4">Featured status, newsletter history and original creation dates on existing records are preserved during import.</p>
      </div>
    </div>
  );
};
